/**
 * Clock formatting shared by the hero status pill and the visit hours card.
 *
 * shop.config.json stores hours as 24-hour strings ("09:00"), which is the
 * right storage form and the wrong display form — "09:00 – 18:00" reads like
 * a train timetable on a barbershop's front page.
 *
 * Lives here rather than in either component because both need it and a
 * second copy would drift the moment one of them changed.
 */

/** "09:00" → "9am"; "16:30" → "4:30pm". Locale-aware via Intl. */
export function shortTime(hhmm: string, locale: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  // Fixed UTC date + UTC timeZone: this formats a wall-clock value, not an
  // instant, so the machine's zone must not shift it.
  const d = new Date(Date.UTC(2000, 0, 1, h, m));
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: m === 0 ? undefined : '2-digit',
    hour12: true,
    timeZone: 'UTC',
  })
    .format(d)
    // Intl gives "9 AM" for en and "9 a. m." for es. Both are stripped to the
    // same compact form: the Spanish default renders as "9a.m." once the
    // spaces go, which reads as a typo in a tight hours row, and "9am" is
    // unambiguous to the bilingual audience this shop actually serves.
    .replace(/[\s.]/g, '')
    .toLowerCase();
}

/** "09:00", "18:00" → "9am – 6pm". En dash, spaced, per COPY_STYLE. */
export function timeRange(open: string, close: string, locale: string): string {
  return `${shortTime(open, locale)} – ${shortTime(close, locale)}`;
}

/** Epoch millis → "4:15pm", in the viewer's zone. Used for hold expiry. */
export function clockFromMillis(ms: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', hour12: true })
    .format(new Date(ms))
    .replace(/[\s.]/g, '')
    .toLowerCase();
}
